import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import { useSelector } from 'react-redux';
import { styled } from '@linaria/react';
import { Stack } from '@vio/atlas/helpers/Stack';
import { PopoverPlacement } from '@vio/atlas/Popover';
import { Text } from '@vio/atlas/Text';
import { POPOVER_HORIZONTAL_OFFSET, POPOVER_VERTICAL_OFFSET } from '@vio/shared/src/search/constants';
import { DatePickerPopover } from '@vio/shared/src/search/DatePickerPopover';
import { GuestRoomConfigPopover } from '@vio/shared/src/search/GuestRoomConfig/GuestRoomConfigPopover';
import { SearchFormHorizontal } from '@vio/shared/src/search/SearchFormHorizontal';
import { KiwiSearchForm } from '@vio/shared/src/whiteLabel/KiwiSearchForm/KiwiSearchForm';
import { SearchFormHorizontalFullWidth } from '@vio/shared/src/whiteLabel/SearchFormHorizontalFullWidth';
import { DestinationPickerPopover } from 'components/DestinationPickerPopover';
import addDays from 'date-fns/addDays';
import format from 'date-fns/format';
import { OpenedFormControl, RecentSearchParams, useSearchBox, useSearchBoxDatePicker, useSearchBoxDestinationPicker, useSearchBoxGuestPicker } from 'hooks/searchBox';
import { getMeta } from 'modules/meta/selectors';
import { getSearchParameters, getShowTotalPrices } from 'modules/sapiSearch/selectors';
import { getIsMultiRoomBundlesSearch } from 'modules/search/selectors';
import { getOpenDatePickerType, getSearchBoxDestinationDisplayValue, getSearchBoxValues, getShowRoomsLabel } from 'modules/searchBox/selectors';
import { SearchTrigger } from 'modules/searchBox/types';
import { getUserSearchHistory } from 'utils/persistence';
import { useBrand } from '@daedalus/core/src/_web/brand/hooks/useBrand';
import { useDispatchTeamEvent } from '@daedalus/core/src/analytics/hooks/useDispatchTeamEvent';
import { trackEvent } from '@daedalus/core/src/analytics/modules/actions';
import { TrackEventPayload } from '@daedalus/core/src/analytics/types/Events';
import { DatePickerType } from '@daedalus/core/src/datePicker/types';
import { ConfigureI18nContext } from '@daedalus/core/src/localization/components/ConfigureI18n';
import { FormattedMessageWrapper } from '@daedalus/core/src/localization/components/FormattedMessage';
import { numberOfGuests, roomsSplitToString, roomsToConfigurationObject } from '@daedalus/core/src/room/business/roomConfiguration';
import { RoomConfigType, RoomSplitType } from '@daedalus/core/src/room/types/RoomConfiguration';
import { WithColorPriceDayData } from '@daedalus/core/src/search/modules/ColorPriceCalendar/components/WithColorPriceDayData';
import { useColorPriceCalendar } from '@daedalus/core/src/search/modules/ColorPriceCalendar/hooks';
import { dateToMiddayDate, UTS_DATE_FORMAT } from '@daedalus/core/src/utils/date';
const SearchBoxWrapperBase = /*#__PURE__*/styled('div')({
  name: "SearchBoxWrapperBase",
  class: "sq0wqd0",
  propsAsIs: false
});
const _exp3 = /*#__PURE__*/() => Text;
const InputError = /*#__PURE__*/styled(_exp3())({
  name: "InputError",
  class: "iy8v188",
  propsAsIs: true
});
const isScrollBarClicked = (e: MouseEvent, offset = 20) => e.clientX + offset >= document.documentElement.offsetWidth;
export const SearchFormDesktop = () => {
  const containerRef = useRef<HTMLDivElement>();
  const guestPickerRef = useRef<HTMLDivElement>();
  const destinationInputRef = useRef<HTMLDivElement>();
  const [openedFormControl, setOpenedFormControl] = useState<OpenedFormControl>(OpenedFormControl.NONE);
  const [isGeolocationSearchSelected, setIsGeolocationSearchSelected] = useState(false);
  const {
    brandIsKiwi,
    brandHasAlternativeSearchForm
  } = useBrand();
  const userSearchHistory = getUserSearchHistory();
  const latestUserSearch = userSearchHistory?.[0];
  const today = dateToMiddayDate(new Date());
  const tomorrow = addDays(today, 1);
  const todayString = format(today, UTS_DATE_FORMAT);
  const tomorrowString = format(tomorrow, UTS_DATE_FORMAT);
  const initialDatePickerValues = {
    checkIn: latestUserSearch?.searchParams.checkIn || todayString,
    checkOut: latestUserSearch?.searchParams.checkOut || tomorrowString
  };
  const initialGuestPickerValues = {
    rooms: latestUserSearch?.searchParams.rooms,
    optimizeRooms: latestUserSearch?.searchParams.optimizeRooms
  };
  const {
    doSearch,
    onSearchButtonClick
  } = useSearchBox();
  const {
    destinationHasError,
    onDestinationChange,
    onDestinationPickerInputClick,
    onRecentSearchSelected,
    setDestinationHasError,
    destinationDisplayValue,
    destinationPickerInputDisplayValue,
    onSuggestionsRequested,
    onNearbyPropertiesSuggestionSelected
  } = useSearchBoxDestinationPicker();
  const {
    onDateChange,
    onDatePickerClose,
    onDatePickerDayClick,
    onDatePickerOpen,
    onDatePickerInputClick,
    onDatePickerSubmit,
    onDatePickerReset
  } = useSearchBoxDatePicker(initialDatePickerValues);
  const {
    onGuestRoomsChange,
    onGuestOptimizeRoomsChange,
    onGuestPickerClick,
    onGuestPickerSubmit,
    onGuestPickerReset,
    onGuestPickerDisplayed,
    onGuestPickerOptionInfoDisplayed,
    onGuestConfigPreferenceClick
  } = useSearchBoxGuestPicker(initialGuestPickerValues);
  const {
    checkIn,
    checkOut,
    hotelId,
    placeId,
    rooms,
    address,
    optimizeRooms
  } = useSelector(getSearchBoxValues);
  const searchBoxDestinationDisplayValue = useSelector(getSearchBoxDestinationDisplayValue);
  const showTotalPrices = useSelector(getShowTotalPrices);
  const searchParameters = useSelector(getSearchParameters);
  const combinedSearchParameters = {
    ...searchParameters,
    rooms,
    checkIn,
    checkOut
  };
  const {
    includeLocalTaxes,
    includeTaxes
  } = useSelector(getMeta);
  const isMultiRoomBundlesSearch = useSelector(getIsMultiRoomBundlesSearch);
  const pricingContext = {
    includeTaxes,
    includeLocalTaxes,
    includeRoomsInNightlyPrice: isMultiRoomBundlesSearch
  };
  const numberOfGuestsValue = numberOfGuests(rooms);
  const isColorPriceDatePickerEnabled = numberOfGuestsValue <= 2 && Boolean(hotelId);
  const openedDatePickerType = useSelector(getOpenDatePickerType);
  const {
    monthNames,
    weekDayNamesShort,
    firstDayOfWeek
  } = useContext(ConfigureI18nContext);
  const {
    numberOfAdults,
    numberOfChildren,
    numberOfRooms,
    roomsSplit
  } = roomsToConfigurationObject(rooms);
  const showRoomsLabel = useSelector(getShowRoomsLabel);
  const isDestinationPickerOpen = useMemo(() => openedFormControl === OpenedFormControl.DESTINATION, [openedFormControl]);
  const isCheckInPickerOpen = useMemo(() => openedFormControl === OpenedFormControl.CHECK_IN, [openedFormControl]);
  const isCheckOutPickerOpen = useMemo(() => openedFormControl === OpenedFormControl.CHECK_OUT, [openedFormControl]);
  const isGuestPickerOpen = useMemo(() => openedFormControl === OpenedFormControl.GUEST, [openedFormControl]);
  const dispatchTeamEvent = useDispatchTeamEvent();
  const trackEventCallback = useCallback((payload: TrackEventPayload) => dispatchTeamEvent(trackEvent(payload)), [dispatchTeamEvent]);
  const {
    requestOneMonthAvailability
  } = useColorPriceCalendar({
    searchParameters: combinedSearchParameters,
    pricingContext
  });
  const handleRecentSearchSelected = useCallback((recentSearch: RecentSearchParams): void => {
    onRecentSearchSelected({
      recentSearch,
      additionalValues: {
        homeSearch: '1'
      }
    });
  }, [onRecentSearchSelected]);
  const handleSuggestionSelected = useCallback((): void => {
    setIsGeolocationSearchSelected(false);
    setOpenedFormControl(OpenedFormControl.CHECK_IN);
    onDatePickerOpen(DatePickerType.CheckIn);
  }, [onDatePickerOpen, setIsGeolocationSearchSelected]);
  const handleNearbyPropertiesSuggestionSelected = useCallback((): void => {
    onNearbyPropertiesSuggestionSelected(false);
    setIsGeolocationSearchSelected(true);
    setOpenedFormControl(OpenedFormControl.CHECK_IN);
    onDatePickerOpen(DatePickerType.CheckIn);
  }, [onNearbyPropertiesSuggestionSelected, setIsGeolocationSearchSelected, onDatePickerOpen]);
  const handleDestinationClick = useCallback((): void => {
    setOpenedFormControl(OpenedFormControl.DESTINATION);
    setDestinationHasError(false);
    onDestinationPickerInputClick();
  }, [setDestinationHasError, onDestinationPickerInputClick, setIsGeolocationSearchSelected]);
  const handleCloseDestinationPicker = (e: MouseEvent): void => {
    // do not close the destination picker if we are clicking within the input container or scrollbar
    if (!isScrollBarClicked(e) && !destinationInputRef?.current?.contains(e.target as Node)) {
      handleCloseAllPopovers();
    }
  };
  const handleCheckInClick = useCallback((): void => {
    onDatePickerInputClick(DatePickerType.CheckIn);
    onDatePickerOpen(DatePickerType.CheckIn);
    setOpenedFormControl(OpenedFormControl.CHECK_IN);
  }, [onDatePickerInputClick, onDatePickerOpen]);
  const handleCheckOutClick = useCallback((): void => {
    onDatePickerInputClick(DatePickerType.CheckOut);
    onDatePickerOpen(DatePickerType.CheckOut);
    setOpenedFormControl(OpenedFormControl.CHECK_OUT);
  }, [onDatePickerInputClick, onDatePickerOpen]);
  const handleCloseDatePicker = useCallback((): void => {
    onDatePickerClose();
  }, [onDatePickerClose]);
  const handleSubmitDatePicker = useCallback((): void => {
    handleCloseAllPopovers();
    onDatePickerSubmit();
  }, [onDatePickerSubmit]);
  const handleGuestPickerClick = useCallback((): void => {
    setOpenedFormControl(OpenedFormControl.GUEST);
    onGuestPickerClick();
    onGuestPickerDisplayed();
  }, [onGuestPickerClick, onGuestPickerDisplayed]);
  const handleCloseAllPopovers = () => setOpenedFormControl(OpenedFormControl.NONE);
  const handleCloseGuestPicker = () => {
    setOpenedFormControl(OpenedFormControl.NONE);
  };
  const handleGuestRoomsChange = useCallback((roomsSplit: RoomSplitType[]): void => {
    const rooms = roomsSplitToString(roomsSplit);
    onGuestRoomsChange(rooms);
  }, [onGuestRoomsChange]);
  const handleGuestOptimizeRoomsChange = useCallback((isOptimizeRooms: boolean): void => {
    onGuestOptimizeRoomsChange(isOptimizeRooms);
  }, [onGuestOptimizeRoomsChange]);
  const handleSubmitGuestPicker = useCallback((): void => {
    handleCloseAllPopovers();
    onGuestPickerSubmit();
  }, [onGuestPickerSubmit]);
  const handleSubmit = useCallback((event?: React.SyntheticEvent | null | undefined): void | boolean => {
    event?.preventDefault();
    handleCloseAllPopovers();
    if (!placeId && !hotelId && !isGeolocationSearchSelected && !address) {
      setDestinationHasError(true);
      return false;
    }

    // to have the DatePicker closed on the search page
    onDatePickerClose();
    onSearchButtonClick();
    doSearch({
      newUrlParams: {
        homeSearch: '1'
      },
      searchTrigger: SearchTrigger.Submit
    });
  }, [onDatePickerClose, doSearch, onSearchButtonClick, setDestinationHasError, searchBoxDestinationDisplayValue, placeId, hotelId, isGeolocationSearchSelected, address]);
  const handleOutsideClick = (e: MouseEvent): void => {
    /** Preventing from closing the popovers by clicking on the scrollbar or if no form controls are open */
    if (!isScrollBarClicked(e) && openedFormControl !== OpenedFormControl.NONE) {
      handleCloseAllPopovers();
      onDatePickerReset();
      onGuestPickerReset();
    }
  };
  const handleDatePickerDayClick = (name: DatePickerType, date: string) => {
    onDatePickerDayClick(name, date);
    setOpenedFormControl(name === DatePickerType.CheckIn ? OpenedFormControl.CHECK_OUT : OpenedFormControl.CHECK_IN);
  };
  const getSearchFormComponent = () => {
    if (brandIsKiwi) {
      return KiwiSearchForm;
    }
    if (brandHasAlternativeSearchForm) {
      return SearchFormHorizontalFullWidth;
    }
    return SearchFormHorizontal;
  };
  const SearchFormComponent = getSearchFormComponent();
  return <>
      <Stack space="s500">
        {brandIsKiwi && <Text variant="titleL">
            <FormattedMessageWrapper id="whereAreYouGoing" defaultMessage="Where are you going?" />
          </Text>}
        <SearchBoxWrapperBase ref={containerRef}>
          <OutsideClickHandler display="contents" onOutsideClick={handleOutsideClick}>
            <SearchFormComponent checkIn={checkIn} checkOut={checkOut} destinationButtonDisplayValue={destinationDisplayValue} destinationInputDisplayValue={destinationPickerInputDisplayValue} numberOfAdults={numberOfAdults} numberOfChildren={numberOfChildren} numberOfRooms={numberOfRooms} onDestinationPickerClick={handleDestinationClick} onCheckInDatePickerClick={handleCheckInClick} onCheckOutDatePickerClick={handleCheckOutClick} onGuestPickerClick={handleGuestPickerClick} onSubmit={handleSubmit} isDestinationPickerOpen={isDestinationPickerOpen} isCheckInPickerOpen={isCheckInPickerOpen} isCheckOutPickerOpen={isCheckOutPickerOpen} isGuestPickerOpen={isGuestPickerOpen} guestPickerRef={guestPickerRef} destinationInputRef={destinationInputRef} onSuggestionsRequested={onSuggestionsRequested} destinationHasError={destinationHasError} isRoomsLabelShown={showRoomsLabel} trackEvent={trackEventCallback} />
            <DestinationPickerPopover isOpen={isDestinationPickerOpen} anchorRef={containerRef} onChange={onDestinationChange} onSuggestionSelected={handleSuggestionSelected} onRecentSearchSelected={handleRecentSearchSelected} onClose={handleCloseDestinationPicker} onNearbyPropertiesSuggestionSelected={handleNearbyPropertiesSuggestionSelected} />
            <DatePickerPopover isOpen={isCheckInPickerOpen || isCheckOutPickerOpen} openedDatePickerType={openedDatePickerType} anchorRef={containerRef} checkIn={checkIn} checkOut={checkOut} onClose={handleCloseDatePicker} onChange={onDateChange} onDatePickerOpen={onDatePickerOpen} onDatePickerDayClick={handleDatePickerDayClick} verticalOffset={POPOVER_VERTICAL_OFFSET} horizontalOffset={POPOVER_HORIZONTAL_OFFSET} weekdaysShort={weekDayNamesShort} months={monthNames} firstDayOfWeek={firstDayOfWeek} onSubmit={handleSubmitDatePicker} submitButtonMessage={<FormattedMessageWrapper id="apply" defaultMessage="Apply" />} showTotalPrices={showTotalPrices} shouldShowPrices onMonthView={date => requestOneMonthAvailability(date, [hotelId])} isColorPriceDatePickerEnabled={isColorPriceDatePickerEnabled} DayInnerContainer={WithColorPriceDayData} />
            <GuestRoomConfigPopover isOpen={isGuestPickerOpen} anchorRef={guestPickerRef} placement={PopoverPlacement.Left} roomsSplit={roomsSplit} onRoomsChange={handleGuestRoomsChange} onOptimizeRoomsChange={handleGuestOptimizeRoomsChange} onClose={handleCloseGuestPicker} onSubmit={handleSubmitGuestPicker} onInfoOpen={onGuestPickerOptionInfoDisplayed} onGuestConfigOptionClick={onGuestConfigPreferenceClick} verticalOffset={POPOVER_VERTICAL_OFFSET} horizontalOffset={POPOVER_HORIZONTAL_OFFSET} submitButtonMessage={<FormattedMessageWrapper id="apply" defaultMessage="Apply" />} roomConfigType={optimizeRooms ? RoomConfigType.Optimized : RoomConfigType.Manual} />
          </OutsideClickHandler>
        </SearchBoxWrapperBase>
      </Stack>
      {destinationHasError && <InputError colorPath="content.danger.c700" variant="bodyXS">
          {searchBoxDestinationDisplayValue ? <FormattedMessageWrapper id="destinationPickerUnknownMessage" defaultMessage="Sorry, we don't recognize that location" /> : <FormattedMessageWrapper id="destinationPickerEmptyMessage" defaultMessage="Tell us where you would like to go" />}
        </InputError>}
    </>;
};

require("./SearchFormDesktop.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./SearchFormDesktop.tsx");